import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  multiFactor,
  getMultiFactorResolver,
  type User,
  type IdTokenResult,
  type MultiFactorResolver,
} from "firebase/auth";
import { getFirebase } from "./firebase";
import { call } from "./api";
import { canonicalOrigin } from "./origin";

export type AdminRole = "platform_admin" | "end_user" | undefined;

export interface AuthState {
  user: User | null;
  claims: Record<string, unknown> | null;
  role: AdminRole;
  mfaEnrolled: boolean;
  loading: boolean;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ ok: true } | { ok: false; mfaResolver: MultiFactorResolver } | { ok: false; error: string }>;
  signOut: () => Promise<void>;
  /** Resolves ok even for an unknown address — see the note in the impl. */
  sendReset: (email: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  refreshClaims: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

type AuthSnapshot = Pick<AuthState, "user" | "claims" | "loading">;

export function AuthProvider({ children }: PropsWithChildren) {
  const { auth } = getFirebase();
  const [snapshot, setSnapshot] = useState<AuthSnapshot>({ user: null, claims: null, loading: true });
  const { user, claims, loading } = snapshot;

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      // Commit `user`, `claims` and `loading` in one setState so no render ever
      // sees `user` non-null with stale `claims` (independent hardening of the
      // sign-out transition; the TOTP bounce itself is fixed in auth-guard.ts).
      const nextClaims = u ? asClaims(await u.getIdTokenResult()) : null;
      // Listener callbacks are not serialised: a sign-out can land while the
      // token fetch above is still in flight. Drop this commit if the SDK has
      // already moved on, otherwise a stale non-null user overwrites null.
      if (auth.currentUser !== u) return;
      setSnapshot({ user: u, claims: nextClaims, loading: false });
    });
    return unsub;
  }, [auth]);

  const refreshClaims = async () => {
    if (!auth.currentUser) return;
    const tok = await auth.currentUser.getIdTokenResult(true);
    setSnapshot((prev) => ({ ...prev, claims: asClaims(tok) }));
  };

  const signIn: AuthState["signIn"] = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return { ok: true };
    } catch (err) {
      const e = err as { code?: string; message?: string };
      if (e.code === "auth/multi-factor-auth-required") {
        const resolver = getMultiFactorResolver(auth, err as never);
        return { ok: false, mfaResolver: resolver };
      }
      console.warn("signIn failed:", e.code, e.message, err);
      return { ok: false, error: friendlyAuthError(e.code, e.message) };
    }
  };

  const signOut = async () => {
    await fbSignOut(auth);
  };

  const sendReset: AuthState["sendReset"] = async (email) => {
    try {
      // The platform's own branded reset email (security_account master
      // template via Mailtrap), not the SDK/Console default. The function
      // answers ok for unknown addresses too, so nothing here can enumerate
      // admin accounts. continueUrl must be on the function's allowlist —
      // the canonical admin origin is.
      await call<{ email: string; continueUrl: string }, { success: boolean }>("sendPasswordResetEmailCustom")({
        email,
        continueUrl: `${canonicalOrigin(window.location)}/login`,
      });
      return { ok: true };
    } catch (err) {
      const e = err as { code?: string; message?: string };
      console.warn("sendReset failed:", e.code, e.message, err);
      // Sign-in copy talks about passwords; this flow has none.
      if (e.code === "functions/invalid-argument") return { ok: false, error: "That email address is not valid." };
      if (e.code === "functions/resource-exhausted") {
        return { ok: false, error: "Too many reset requests. Wait an hour and try again." };
      }
      return { ok: false, error: "Could not send the reset email. Try again in a moment." };
    }
  };

  const role = (claims?.role as AdminRole) ?? undefined;
  const mfaEnrolled = !!user && multiFactor(user).enrolledFactors.length > 0;

  const value = useMemo<AuthState>(
    () => ({
      user,
      claims,
      role,
      mfaEnrolled,
      loading,
      signIn,
      signOut,
      sendReset,
      refreshClaims,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, claims, role, mfaEnrolled, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

function asClaims(tok: IdTokenResult): Record<string, unknown> {
  return { ...tok.claims };
}

export function friendlyAuthError(code: string | undefined, fallback: string | undefined): string {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/invalid-email":
    case "auth/operation-not-allowed":
      return "Incorrect Email or password.";
    case "auth/user-disabled":
      return "This account has been disabled. Contact an administrator.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    case "auth/internal-error":
      return "Something went wrong on our end. Try again in a moment.";
    default:
      return fallback ?? "Sign-in failed. Please try again.";
  }
}
